<template>
	<v-dialog v-model="dialog" fullscreen hide-overlay transition="dialog-bottom-transition">
		<v-card class="fullscreen-dialog">
			<!-- Header escuro superior -->
			<v-toolbar dark color="grey darken-3" class="elevation-0">
				<v-toolbar-title class="text-h6 font-weight-bold">
					<v-icon :color="vademecum.cor || dynamicColor" size="20">mdi-circle</v-icon>
					{{ vademecum.titulo || 'Vade-mécum' }}
				</v-toolbar-title>
				<v-spacer></v-spacer>
				<!-- Visibilidade -->
				<v-icon :color="vademecum.status === 'public' ? '#4CAF50' : '#757575'" class="mr-2">
					{{ vademecum.status === 'public' || vademecum.status == "published" ? 'mdi-eye' : 'mdi-eye-off' }}
				</v-icon>
				<span class="text-body-2 mr-2" style="min-width:70px;">
					{{ getStatusText(vademecum.status) }}
				</span>
				<v-switch 
					v-if="!vademecum.locked && isAdmin"
					:input-value="vademecum.status === 'public'"
					@change="toggleVadeMecumStatus" 
					color="#F15A24" 
					hide-details 
					style="margin-top:-2px" 
				/>
				<v-spacer></v-spacer>
				<v-btn icon @click="closeDialog">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>

			<!-- Header branco com busca e ações -->
			<v-card class="elevation-0 pa-4" style="background-color: #f8f9fa;">
				<v-row>
					<!-- Botões -->
					<v-col cols="12" md="6" class="d-flex justify-start align-center mobileConfig" v-if="!vademecum.locked && isAdmin">
						<v-btn color="#F15A24" dark @click="$emit('add-lei')" class="mr-2">
							<v-icon class="mr-1">mdi-plus</v-icon>
							Adicionar legislação
						</v-btn>
						<v-btn outlined color="#F15A24" @click="$emit('add-projeto-lei')">
							<v-icon color="#F15A24" class="mr-1">mdi-plus</v-icon>
							Adicionar projeto de lei
						</v-btn>
					</v-col>
					<v-col cols="12" md="6" class="d-flex justify-end mobileConfig" v-if="vademecum.locked && isAdmin">
						<v-btn 
							color="#F15A24" 
							style="color:white;" 
							class="mr-2"
							:loading="loadingImport" 
							:disabled="loadingImport" 
							@click="$emit('import')">
							<v-icon class="mr-1">mdi-import</v-icon>
							Importar vade-mécun
						</v-btn>
					</v-col>
					<!-- Busca -->
					<v-col cols="12" md="6">
						<v-text-field
							clearable
							v-model="buscaVadeMecum"
							:append-icon="loadingSearch ? 'mdi-loading' : 'mdi-magnify'"
							:loading="loadingSearch"
							:color="dynamicColor"
							hide-details
							outlined
							placeholder="Buscar por termo específico em todas legislações do seu vade-mécun"
							label="Buscar por termo específico em todas legislações do seu vade-mécun"
						/>
					</v-col>
				</v-row>
			</v-card>

			<!-- Conteúdo principal -->
			<v-card-text class="pa-6" style="background-color: white;">
				<!-- Seção de Leis -->
				<div class="mb-6">
					<h3 class="text-h5 font-weight-bold mb-4" style="color: #424242;">
						Leis ({{ vademecum.leis ? vademecum.leis.length : 0 }})
						<span v-if="buscaVadeMecum && filteredLeisInVade.length > 0" class="text-caption" style="color: #F15A24; font-weight: normal;">
							- {{ filteredLeisInVade.length }} {{ filteredLeisInVade.length === 1 ? 'resultado encontrado' : 'resultados encontrados' }}
						</span>
					</h3>
					
					<div v-if="!vademecum.leis || vademecum.leis.length === 0" class="empty-state-box pa-4 text-center">
						<p class="mb-0 grey--text">
							Este vade-mécum ainda não possui legislações. Adicione legislações clicando no botão acima ou importando um dos vade-mécuns sugeridos pelo mylegis abaixo.
						</p>
					</div>

					<!-- Mensagem quando busca não retorna resultados -->
					<div v-else-if="buscaVadeMecum && filteredLeisInVade.length === 0" class="empty-state-box pa-4 text-center">
						<v-icon large color="grey">mdi-magnify</v-icon>
						<p class="mb-0 grey--text mt-2">
							Nenhuma lei encontrada com o termo "<strong>{{ buscaVadeMecum }}</strong>".
						</p>
						<p class="mb-0 grey--text text-caption mt-1">
							A busca procura em todo o conteúdo das leis (artigos, parágrafos, incisos, etc.)
						</p>
					</div>

					<div v-else>
						<v-card v-for="lei in filteredLeisInVade" :key="lei.id" outlined class="mb-3" style="border-radius: 8px;">
							<v-card-title class="d-flex justify-end" style="padding: 0 !important; margin: 0 !important;">
								<v-menu offset-y v-if="isAdmin">
									<template v-slot:activator="{ on, attrs }">
										<v-btn icon v-bind="attrs" v-on="on" style="color: #000 !important">
											<v-icon>mdi-dots-vertical</v-icon>
										</v-btn>
									</template>
									<v-list>
										<v-list-item>
											<v-btn icon small text color="grey" @click="excluirLei(lei)">
												<v-icon>mdi-delete</v-icon>
											</v-btn>
										</v-list-item>
									</v-list>
								</v-menu>
							</v-card-title>
							<v-card-text class="pa-4">
								<div class="d-flex justify-space-between align-start displayMobile">
									<div class="flex-grow-1">
										<v-btn
											style="background-color: transparent; box-shadow: none; padding: 0; white-space: normal; display: block; text-align: left; justify-content: start;"
											:to="`/painel/lei/${lei.leis_id_lei.id_lei}/${sanitizeTitle(lei.leis_id_lei.numero_lei)}/${sanitizeTitle(vademecum.titulo)}`">
											<h4 class="text-h6 font-weight-bold mb-2" style="color: #424242; white-space: normal; word-wrap: break-word; overflow-wrap: break-word;">
												{{ lei.leis_id_lei.numero_lei }}, DE {{ formatDate(lei.leis_id_lei.data_lei) }}
											</h4>
										</v-btn>
										<p class="texto-multilinha mb-3 mt-10">
											{{ lei && lei.leis_id_lei ? lei.leis_id_lei.nome_lei : '' }}
										</p>

										<!-- Resultados da busca no conteúdo -->
										<div v-if="buscaVadeMecum && lei.retornoBusca && lei.retornoBusca.length > 0" class="mt-4 pa-3" style="background-color: #FFF8F5; border-left: 3px solid #F15A24; border-radius: 4px;">
											<div class="mb-2">
												<v-icon small color="#F15A24">mdi-text-search</v-icon>
												<strong class="ml-2" style="color: #F15A24;">{{ lei.retornoBusca.length }} {{ lei.retornoBusca.length === 1 ? 'ocorrência encontrada' : 'ocorrências encontradas' }}</strong>
											</div>
											<v-divider class="mb-3"></v-divider>
											<div v-for="(resultado, idx) in lei.retornoBusca.slice(0, 3)" :key="idx" class="mb-3 pa-2 search-result-item" style="background-color: white; border-radius: 4px; cursor: pointer; transition: all 0.2s;" @click="$router.push(getArtigoUrl(lei, resultado))" @mouseenter="$event.currentTarget.style.backgroundColor = '#FFF8F5'" @mouseleave="$event.currentTarget.style.backgroundColor = 'white'">
												<div class="d-flex justify-space-between align-center mb-1">
													<div class="text-caption grey--text text--darken-2" style="font-weight: 600;">
														<v-icon x-small color="#F15A24">mdi-file-document-outline</v-icon>
														{{ resultado.tipo_conteudo || resultado.tipo || 'Artigo' }} {{ resultado.numero_conteudo || resultado.numero || '' }}
													</div>
													<v-icon x-small color="#F15A24">mdi-chevron-right</v-icon>
												</div>
												<div class="text-body-2" style="line-height: 1.6; color: #424242;" v-html="highlight(extractContext(resultado.texto_conteudo || resultado.texto || resultado.conteudo || '', buscaVadeMecum, 150), buscaVadeMecum)"></div>
											</div>
											<div v-if="lei.retornoBusca.length > 3" class="text-caption text-center mt-2" style="color: #F15A24; font-weight: 500;">
												+ {{ lei.retornoBusca.length - 3 }} {{ lei.retornoBusca.length - 3 === 1 ? 'outra ocorrência' : 'outras ocorrências' }} (clique em "ver" para visualizar todas)
											</div>
										</div>

										<!-- Apelido ou criar apelido -->
										<div v-if="lei.leis_id_lei.apelidos_leis && lei.leis_id_lei.apelidos_leis.length > 0 && typeof lei.leis_id_lei.apelidos_leis[0] === 'object' && lei.leis_id_lei.apelidos_leis[0].apelido">
											<v-text-field 
												v-model="lei.leis_id_lei.apelidos_leis[0].apelido" 
												dense
												outlined 
												hide-details 
												:color="dynamicColor" 
												style="max-width: 200px;"
												@blur="salvarApelido(lei.leis_id_lei.id_lei, lei.leis_id_lei.apelidos_leis[0].apelido)"
												@keyup.enter="salvarApelido(lei.leis_id_lei.id_lei, lei.leis_id_lei.apelidos_leis[0].apelido)">
												<template v-slot:append>
													<v-btn icon small @click="salvarApelido(lei.leis_id_lei.id_lei, lei.leis_id_lei.apelidos_leis[0].apelido)">
														<v-icon :color="dynamicColor" small>mdi-check</v-icon>
													</v-btn>
												</template>
											</v-text-field>
										</div>
										<div v-else>
											<v-btn @click="criarApelido(lei)" text x-small v-if="!lei.leis_id_lei.apelidos_leis || lei.leis_id_lei.apelidos_leis.length === 0 || (typeof lei.leis_id_lei.apelidos_leis[0] !== 'object')">
												<v-icon :color="dynamicColor" x-small text class="mr-1">mdi-plus</v-icon>
												Criar apelido
											</v-btn>
											<v-text-field 
												v-if="editNickVadeMecum === lei.leis_id_lei.id_lei"
												v-model="novoApelido" 
												dense 
												outlined 
												hide-details 
												:color="dynamicColor"
												style="max-width: 200px;" 
												placeholder="Digite o apelido"
												@blur="salvarNovoApelido(lei.leis_id_lei.id_lei)"
												@keyup.enter="salvarNovoApelido(lei.leis_id_lei.id_lei)"
												@keyup.escape="cancelarApelido(lei)">
												<template v-slot:append>
													<v-btn icon small @click="salvarNovoApelido(lei.leis_id_lei.id_lei)">
														<v-icon :color="dynamicColor" small>mdi-check</v-icon>
													</v-btn>
													<v-btn icon small @click="cancelarApelido(lei)">
														<v-icon color="grey" small>mdi-close</v-icon>
													</v-btn>
												</template>
											</v-text-field>
										</div>
									</div>

									<!-- Tags e ações -->
									<div class="d-flex flex-column">
										<span style="display: flex; gap: 10px; align-items: center;">
											<v-chip :color="dynamicColor" outlined text-color="#F15A24" style="height: 1.5rem">
												{{ lei.leis_id_lei.esfera_lei }}
											</v-chip>
											<v-chip class="ml-2" outlined :color="dynamicColor" text-color="#F15A24" v-if="lei.leis_id_lei.esfera_lei === `Estadual`" style="height: 1.5rem">
												{{ lei.leis_id_lei.estado_lei }}
											</v-chip>
										</span>
									</div>
								</div>
							</v-card-text>
							<v-card-actions class="d-flex justify-end p-2 mr-5">
								<v-btn small text :color="dynamicColor" :to="`/painel/lei/${lei.leis_id_lei.id_lei}/${sanitizeTitle(lei.leis_id_lei.numero_lei)}/${sanitizeTitle(vademecum.titulo)}`">
									<v-icon>mdi-file-document</v-icon> ver
								</v-btn>
							</v-card-actions>
						</v-card>
					</div>
				</div>

				<!-- Seção de Projetos de Lei -->
				<div class="mb-6">
					<h3 class="text-h5 font-weight-bold mb-4" style="color: #424242;">
						Projetos de Lei
						<span v-if="buscaVadeMecum && filteredProjetosInVade.length > 0 && vademecum.projetos_leis && vademecum.projetos_leis.length > 0" class="text-caption" style="color: #F15A24; font-weight: normal;">
							- {{ filteredProjetosInVade.length }} {{ filteredProjetosInVade.length === 1 ? 'resultado encontrado' : 'resultados encontrados' }}
						</span>
					</h3>
					<div class="empty-state-box pa-4 text-center" v-if="!vademecum.projetos_leis || vademecum.projetos_leis.length === 0">
						<p class="mb-0 grey--text">Este vade-mécum ainda não possui projetos de lei. Adicione projetos de lei clicando no botão acima.</p>
					</div>

					<!-- Mensagem quando busca não retorna resultados -->
					<div v-else-if="buscaVadeMecum && filteredProjetosInVade.length === 0" class="empty-state-box pa-4 text-center">
						<v-icon large color="grey">mdi-magnify</v-icon>
						<p class="mb-0 grey--text mt-2">
							Nenhum projeto de lei encontrado com o termo "<strong>{{ buscaVadeMecum }}</strong>".
						</p>
						<p class="mb-0 grey--text text-caption mt-1">
							A busca procura no número, nome e autor dos projetos de lei.
						</p>
					</div>
				</div>
				<div v-if="vademecum.projetos_leis && vademecum.projetos_leis.length > 0">
					<v-card v-for="lei in filteredProjetosInVade" :key="lei.id" outlined class="mb-3" style="border-radius: 8px;">
						<v-card-title class="d-flex justify-end" style="padding: 0 !important; margin: 0 !important;">
							<v-menu offset-y v-if="isAdmin">
								<template v-slot:activator="{ on, attrs }">
									<v-btn icon v-bind="attrs" v-on="on" style="color: #000 !important">
										<v-icon>mdi-dots-vertical</v-icon>
									</v-btn>
								</template>
								<v-list>
									<v-list-item>
										<v-btn icon small text color="grey" :loading="lei.deleting" @click="excluirProjetoLei(lei)">
											<v-icon>mdi-delete</v-icon>
										</v-btn>
									</v-list-item>
								</v-list>
							</v-menu>
						</v-card-title>
						<v-card-text class="pa-4">
							<div class="d-flex justify-space-between align-start displayMobile">
								<div class="flex-grow-1">
									<v-btn
										style="background-color: transparent; box-shadow: none; padding: 0; white-space: normal; display: block; text-align: left; justify-content: start;"
										:to="`/painel/projeto_lei/${lei.projetos_leis_id.id}/${sanitizeTitle(lei.projetos_leis_id.numero)}/${sanitizeTitle(vademecum.titulo)}`"
									>
										<h4 class="text-h6 font-weight-bold mb-2" style="color: #424242;">{{ lei.projetos_leis_id.numero }}</h4>
									</v-btn>
									<p class="text-body-1 mb-3" style="color: #666;">{{ lei.projetos_leis_id.nome }}</p>
								</div>
								<div class="d-flex flex-column align-end">
									<span style="display: flex; align-items: center;">
										<v-chip :color="dynamicColor" outlined text-color="#F15A24" style="height: 1.5rem">{{ lei.projetos_leis_id.esfera }}</v-chip>
										<v-chip class="ml-2" outlined :color="dynamicColor" text-color="#F15A24" v-if="lei.projetos_leis_id.esfera === `Estadual`" style="height: 1.5rem">{{ lei.projetos_leis_id.estado }}</v-chip>
									</span>
								</div>
							</div>
						</v-card-text>
						<v-card-actions class="d-flex justify-end p-2 mr-5">
							<v-btn small text color="#424242" @click.stop="verProjetoLei(lei)" :disabled="navigating">
								<v-icon>mdi-file-document</v-icon> ver
							</v-btn>
						</v-card-actions>
					</v-card>
				</div>
			</v-card-text>
		</v-card>
	</v-dialog>
</template>

<script>
export default {
	name: 'DialogVerLeis',
	props: {
		value: {
			type: Boolean,
			default: false,
		},
		vademecum: {
			type: Object,
			default: () => ({ leis: [], projetos_leis: [], edit: false }),
		},
		isAdmin: {
			type: Boolean,
			default: false,
		},
		dynamicColor: {
			type: String,
			default: '#F15A24',
		},
	},
	data() {
		return {
			dialog: this.value,
			buscaVadeMecum: '',
			loadingSearch: false,
			loadingImport: false,
			searchTimeout: null,
			editNickVadeMecum: 0,
			novoApelido: '',
			navigating: false,
		};
	},
	computed: {
		filteredLeisInVade() {
			const q = (this.buscaVadeMecum || '').trim().toLowerCase();
			if (!q) {
				return this.vademecum.leis || [];
			}
			// Durante o loading, mostrar todas as leis
			if (this.loadingSearch) {
				return this.vademecum.leis || [];
			}
			// Filtrar apenas leis que têm resultados da busca no banco de dados
			return (this.vademecum.leis || []).filter((rel) => {
				// Buscar por conteúdo (se houver resultados de busca no conteúdo)
				return rel.retornoBusca && rel.retornoBusca.length > 0;
			});
		},
		filteredProjetosInVade() {
			const q = (this.buscaVadeMecum || '').trim().toLowerCase();
			if (!q) return this.vademecum.projetos_leis || [];
			return (this.vademecum.projetos_leis || []).filter((rel) => {
				const p = rel && rel.projetos_leis_id ? rel.projetos_leis_id : {};
				const numero = (p.numero || '').toString().toLowerCase();
				const nome = (p.nome || '').toLowerCase();
				const autor = (p.autor || '').toLowerCase();
				return numero.includes(q) || nome.includes(q) || autor.includes(q);
			});
		},
	},
	watch: {
		value(newVal) {
			this.dialog = newVal;
			if (!newVal) {
				// Limpar busca quando fechar
				this.buscaVadeMecum = '';
				this.loadingSearch = false;
				if (this.searchTimeout) {
					clearTimeout(this.searchTimeout);
				}
			}
		},
		dialog(newVal) {
			this.$emit('input', newVal);
		},
		buscaVadeMecum: {
			handler(newValue, oldValue) {
				// Limpar resultados anteriores quando o campo é limpo
				if (!newValue || newValue.trim() === '') {
					this.loadingSearch = false;
					if (this.vademecum && this.vademecum.leis) {
						this.vademecum.leis.forEach((lei) => {
							lei.retornoBusca = undefined;
						});
					}
					this.$forceUpdate();
					return;
				}
				if (oldValue == newValue) {
					return;
				}
				// Buscar no conteúdo das leis com debounce
				if (this.searchTimeout) {
					clearTimeout(this.searchTimeout);
				}
				
				this.loadingSearch = true;
				this.searchTimeout = setTimeout(() => {
					this.searchConteudoVadeMecum();
				}, 300); // Debounce de 300ms
			},
			immediate: false,
		},
	},
	methods: {
		closeDialog() {
			this.dialog = false;
		},
		getStatusText(status) {
			switch (status) {
				case 'published':
					return 'Público';
				case 'public':
					return 'Público';
				case 'private':
					return 'Privado';
				default:
					return 'Privado';
			}
		},
		toggleVadeMecumStatus(isPublic) {
			const newStatus = isPublic ? 'public' : 'private';
			this.$emit('update-status', newStatus);
		},
		async searchConteudoVadeMecum() {
			if (this.buscaVadeMecum === '' || !this.vademecum.leis || this.vademecum.leis.length === 0) {
				return;
			}
			this.loadingSearch = true;
			try {
				const response = await this.$http.get(
					`items/conteudoleis/?search=${this.buscaVadeMecum}&limit=-1&filter[id_lei][_in]=${this.vademecum.leis.map((e) => e.leis_id_lei.id_lei).toString()}`
				);
				
				let resultadoBusca = [];
				if (response.data && response.data.data && response.data.data.length > 0) {
					response.data.data.forEach((el) => {
						if (resultadoBusca[el.id_lei] === undefined)
							resultadoBusca[el.id_lei] = [];
						resultadoBusca[el.id_lei].push(el);
					});
				}
				// Atualizar resultados de busca para todas as leis
				this.vademecum.leis.forEach((lei) => {
					if (resultadoBusca[lei.leis_id_lei.id_lei] === undefined || resultadoBusca[lei.leis_id_lei.id_lei].length === 0) {
						lei.retornoBusca = [];
					} else {
						lei.retornoBusca = resultadoBusca[lei.leis_id_lei.id_lei];
					}
				});
				this.loadingSearch = false;
				this.$forceUpdate();
			} catch (error) {
				window.console.error('Erro ao buscar conteúdo:', error);
				this.loadingSearch = false;
				// Limpar resultados em caso de erro
				if (this.vademecum && this.vademecum.leis) {
					this.vademecum.leis.forEach((lei) => {
						lei.retornoBusca = [];
					});
				}
				this.$forceUpdate();
			}
		},
		highlight(content, query) {
			if (!query) {
				return content;
			}
			return content.replace(new RegExp(query, 'gi'), (match) => {
				return '<span class="my-orange">' + match + '</span>';
			});
		},
		extractContext(text, query, contextLength = 150) {
			if (!text || !query) return text || '';
			
			const normalizedText = text.trim();
			const searchQuery = query.trim();
			const lowerText = normalizedText.toLowerCase();
			const lowerQuery = searchQuery.toLowerCase();
			const position = lowerText.indexOf(lowerQuery);
			
			if (position === -1 || normalizedText.length <= contextLength * 2) {
				return normalizedText;
			}
			
			let start = Math.max(0, position - contextLength);
			let end = Math.min(normalizedText.length, position + searchQuery.length + contextLength);
			
			if (start > 0) {
				const spaceAfterStart = normalizedText.indexOf(' ', start);
				if (spaceAfterStart !== -1 && spaceAfterStart < position) {
					start = spaceAfterStart + 1;
				}
			}
			
			if (end < normalizedText.length) {
				const spaceBeforeEnd = normalizedText.lastIndexOf(' ', end);
				if (spaceBeforeEnd > position + searchQuery.length) {
					end = spaceBeforeEnd;
				}
			}
			
			let context = normalizedText.substring(start, end);
			if (start > 0) context = '...' + context;
			if (end < normalizedText.length) context = context + '...';
			
			return context;
		},
		getArtigoUrl(lei, resultado) {
			// Incluir vade-mecum na URL se disponível
			const vadeMecumSlug = this.vademecum && this.vademecum.titulo 
				? `/${this.sanitizeTitle(this.vademecum.titulo)}` 
				: '';
			const baseUrl = `/painel/lei/${lei.leis_id_lei.id_lei}/${this.sanitizeTitle(lei.leis_id_lei.numero_lei)}${vadeMecumSlug}`;
			
			if (resultado.tipo_conteudo && resultado.numero_conteudo) {
				const artigoId = `${resultado.tipo_conteudo.toLowerCase()}-${resultado.numero_conteudo}`;
				return `${baseUrl}?id=${resultado.id_reg || resultado.id}`;
			}
			
			return baseUrl;
		},
		sanitizeTitle(title) {
			if (!title) return '';
			var slug = '';
			var titleLower = title.toLowerCase();
			slug = titleLower.replace(/e|é|è|ẽ|ẻ|ẹ|ê|ế|ề|ễ|ể|ệ/gi, 'e');
			slug = slug.replace(/a|á|à|ã|ả|ạ|ă|ắ|ằ|ẵ|ẳ|ặ|â|ấ|ầ|ẫ|ẩ|ậ/gi, 'a');
			slug = slug.replace(/o|ó|ò|õ|ỏ|ọ|ô|ố|ồ|ỗ|ổ|ộ|ơ|ớ|ờ|ỡ|ở|ợ/gi, 'o');
			slug = slug.replace(/u|ú|ù|ũ|ủ|ụ|ư|ứ|ừ|ữ|ử|ự/gi, 'u');
			slug = slug.replace(/đ/gi, 'd');
			slug = slug.replace(/\s*$/g, '');
			slug = slug.replace(/\s+/g, '-');
			return encodeURIComponent(slug);
		},
		formatDate(dateString) {
			if (!dateString) return '';
			const date = new Date(dateString);
			return date.toLocaleDateString('pt-BR');
		},
		excluirLei(lei) {
			this.$emit('excluir-lei', lei, this.vademecum.id);
		},
		excluirProjetoLei(lei) {
			this.$emit('excluir-projeto-lei', lei, this.vademecum.id);
		},
		verProjetoLei(lei) {
			if (this.navigating) {
				return;
			}
			
			this.navigating = true;
			const url = `/painel/projeto_lei/${lei.projetos_leis_id.id}/${this.sanitizeTitle(lei.projetos_leis_id.numero)}/${this.sanitizeTitle(this.vademecum.titulo)}`;
			
			this.dialog = false;
			
			this.$nextTick(() => {
				setTimeout(() => {
					this.$router.push(url).catch(err => {
						if (err.name !== 'NavigationDuplicated' && !err.message.includes('Avoided redundant navigation')) {
							window.console.error('Erro ao navegar:', err);
						}
					}).finally(() => {
						this.navigating = false;
					});
				}, 150);
			});
		},
		postNick(idLei, nick) {
			this.$http
				.post(`items/apelidos_leis`, {
					status: 'Publicado',
					lei: idLei,
					apelido: nick,
				})
				.then(() => {
					this.editNickVadeMecum = 0;
					this.$emit('reload');
				});
		},
		criarApelido(lei) {
			this.editNickVadeMecum = lei.leis_id_lei.id_lei;
			this.novoApelido = '';
			this.$nextTick(() => {
				const input = document.querySelector('input[placeholder="Digite o apelido"]');
				if (input) {
					input.focus();
				}
			});
		},
		salvarApelido(idLei, apelido) {
			if (apelido && apelido.trim() !== '') {
				this.postNick(idLei, apelido.trim());
			}
		},
		salvarNovoApelido(idLei) {
			if (this.novoApelido && this.novoApelido.trim() !== '') {
				this.postNick(idLei, this.novoApelido.trim());
				this.editNickVadeMecum = 0;
				this.novoApelido = '';
			} else {
				this.cancelarApelido();
			}
		},
		cancelarApelido() {
			this.editNickVadeMecum = 0;
			this.novoApelido = '';
		},
	},
};
</script>

<style scoped>
.empty-state-box {
	border: 1px dashed #e0e0e0;
	border-radius: 8px;
	background-color: #fafafa;
}

.search-result-item:hover {
	background-color: #FFF8F5 !important;
}
</style>

